import { Trans, useTranslation } from 'react-i18next'
import MilestoneProgressBar from './MilestoneProgressBar'
import { type Milestone } from './milestoneHelpers'
import LevelTracker from './LevelTracker/LevelTracker'
import { buildLevels } from './LevelTracker/levelHelpers'
import { LEVEL_DEFINITIONS } from './LevelTracker/levelDefinitions'
import type { LevelTrackerLabels } from './LevelTracker/levelTrackerTypes'
import { resolveContributionDisplay } from './contributionDisplay'

import { useContributions } from '@hooks/index'
import { calculateDaysRemaining } from '@/util/helpers'
import { flags } from '@/util/featureFlags'
import { sendLevelPopoverOpenEvent } from '@/services/analytics/gtm'

const COPY = 'content.components.contribution_progress'

export default function ContributionProgressBar() {
  const { t } = useTranslation()
  const { data } = useContributions()

  // `goals.contributionGoal` is always populated: the transformer falls back to
  // the flat legacy goal when the API omits it. While the query is loading,
  // render an empty single-node bar.
  const contributionGoal = data?.goals.contributionGoal
  const rungs = contributionGoal?.goals ?? [0]
  const target = contributionGoal?.target ?? 0
  const amount = contributionGoal?.progress ?? 0
  const lastRung = rungs[rungs.length - 1] ?? 0
  // The header counts down to the next unmet rung, or the last rung once all are cleared.
  const goal = rungs.find((g) => g > amount) ?? lastRung
  const max = lastRung

  const milestones: Milestone[] = rungs.map((g, index) => {
    if (g === target) {
      return { position: g, variant: 'books' }
    }

    if (rungs.length >= 3 && index + 1 === rungs.length) {
      return { position: g, variant: 'medal', color: 'green' }
    }

    // fallback: party popper for all other milestones
    return { position: g, variant: 'goal' }
  })

  const disableDate = data?.disableDate ?? null
  const contributorCount = data?.contributions.length ?? 0
  const daysRemaining = calculateDaysRemaining(disableDate)

  // Levels: amounts from the goal ladder, count/order/icons from the mirrored
  // definitions, text from i18n keyed by each definition's `key` (ARD #4, #17).
  const levels = buildLevels(
    contributionGoal?.goals ?? [],
    LEVEL_DEFINITIONS.map((definition) => ({
      ...definition,
      title: t(`${COPY}.levels.${definition.key}.title`),
      description: t(`${COPY}.levels.${definition.key}.description`),
    })),
  )
  const display = resolveContributionDisplay(contributionGoal, flags, levels.length)

  const levelLabels: LevelTrackerLabels = {
    received: t(`${COPY}.current_progress_text`),
    toNextLevel: t(`${COPY}.to_next_level`),
    toGoal: t(`${COPY}.goal_text`),
    allLevelsUnlocked: (
      <Trans i18nKey={`${COPY}.all_levels_unlocked`} components={{ em: <span className='text-alt-txt font-bold' /> }} />
    ),
    unlockFirstLevel: t(`${COPY}.unlock_first_level`),
    levelsUnlocked: (earned, total) => t(`${COPY}.levels_unlocked`, { earned, total }),
    levelTitle: (level, title) => t(`${COPY}.level_title`, { level, title }),
    badgeLabel: (level, title, unlocked) =>
      t(unlocked ? `${COPY}.level_badge_unlocked` : `${COPY}.level_badge_locked`, { level, title }),
    contributors: (count) => t(`${COPY}.contributor_count`, { count }),
    daysRemaining: (days) => ({ value: String(days), label: t(`${COPY}.countdown`, { count: days }) }),
  }

  return (
    <div id='contribution-progress-bar' className='text-center'>
      <div className='text-cta mb-6 text-xl font-serif '>{t('content.components.contribution_progress.subtitle')}</div>

      {display === 'levels' ? (
        <LevelTracker
          received={amount}
          levels={levels}
          contributors={contributorCount}
          daysRemaining={daysRemaining}
          labels={levelLabels}
          onLevelPopoverOpen={sendLevelPopoverOpenEvent}
        />
      ) : (
        <>
          <div className='flex items-end justify-between gap-4'>
            <div className='flex items-baseline gap-2'>
              <span className='text-cta text-2xl font-serif font-bold'>${amount}</span>
              <span className='text-sm text-secondary-txt'>
                {t('content.components.contribution_progress.current_progress_text')}
              </span>
            </div>
            {goal > amount ? (
              <div className='flex items-baseline gap-2 text-right'>
                <span className='text-cta text-2xl font-serif font-bold'>${goal - amount}</span>
                <span className='text-sm text-secondary-txt'>
                  {t('content.components.contribution_progress.goal_text')}
                </span>
              </div>
            ) : (
              <div className='flex items-baseline gap-2 text-right'>
                <span className='text-cta text-2xl font-serif font-bold'>
                  {t('content.components.contribution_progress.goal_achieved')}
                </span>
              </div>
            )}
          </div>

          <MilestoneProgressBar value={amount} max={max} milestones={milestones} />

          <div className='pt-6 flex items-end justify-between gap-4'>
            <div className='flex items-baseline gap-2'>
              <span className='text-cta text-2xl font-serif font-bold'>{contributorCount}</span>
              <span className='text-sm text-secondary-txt'>
                {t('content.components.contribution_progress.contributor_count', { count: contributorCount })}
              </span>
            </div>
            <div className='flex items-baseline gap-2 text-right'>
              <span className='text-cta text-2xl font-serif font-bold'>{daysRemaining}</span>
              <span className='text-sm text-secondary-txt'>
                {t('content.components.contribution_progress.countdown', { count: daysRemaining })}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
